import React from "react";
import { Link } from "react-router-dom";
import { OutstandingText } from "../OutstandingPeople";
import { StatusBadge } from "./StatusBadge";

interface RecentRecordCardProps {
  record: {
    id: string;
    customer: string;
    reference: string;
    status: string;
    riskLevel: string;
    createdDate: string;
    /**
     * AL-455: how many people the C1 can still act on, and how many there are in
     * total. Same source as the records list — both come through
     * mapRawToBlotterRecord, so the two surfaces cannot disagree.
     *
     * Optional because the backend omits them rather than sending zero when the
     * rollup could not be computed. Absent must render as nothing: "0
     * outstanding" against an unknown value is a false all-clear.
     */
    totalPeople?: number;
    outstandingPeople?: number;
  };
}

const RecentRecordCard: React.FC<RecentRecordCardProps> = ({ record }) => {
  /**
   * AL-455: requires a real number, because absent is not zero. The backend omits
   * these when the rollup could not be computed, and treating that as "none
   * outstanding" would tell a C1 everything is done when we do not know.
   */
  const hasOutstanding =
    typeof record.outstandingPeople === "number" && record.outstandingPeople > 0;

  return (
  <div className="record-card-glass" data-testid={`record-card-${record.id}`}>
    <div className="record-header">
      <div className="record-content">
        <h3 className="record-title" data-testid={`record-title-${record.id}`}>
          {record.customer}
        </h3>
        <p className="record-ref">Reference: {record.reference}</p>
        {/*
          AL-455: a sentence under the reference rather than a badge beside the
          status. A card has room to say what is happening, and a second badge
          next to the status one made the reader compare two labels to work out
          which mattered.

          Not interactive: the card is itself a link to the record, and a button
          inside it is a nested-activation problem. The per-person breakdown is
          one click away on the records list.
        */}
        <OutstandingText
          recordId={record.id}
          totalPeople={record.totalPeople}
          outstandingPeople={record.outstandingPeople}
        />
      </div>
      <div className="record-badges">
        <StatusBadge status={record.status} />
      </div>
    </div>

    {record.status === "in-progress" ? (
      <div
        className="record-footer"
        style={{
          background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <span style={{ fontSize: "1.1rem" }}>⏳</span>
        <div style={{ flex: 1 }}>
          {/*
            AL-455: this used to say "Report is being generated" for every
            in-progress case. normalizeCDDStatus maps data_collection,
            checks_in_progress and remediation all to 'in-progress', and in none of
            those is a report being generated — so the more specific the wording
            got, the more often it was false. It now says what is actually true of
            all of them: the case is still running.

            It also no longer restates the waiting message, which the line under
            the reference already carries. Two messages about one case is what made
            the table row contradict itself.
          */}
          <p
            style={{
              margin: 0,
              fontSize: "0.82rem",
              fontWeight: 600,
              color: "#1e40af",
            }}
            data-testid={`record-progress-${record.id}`}
          >
            {hasOutstanding
              ? "Waiting on your client — see Records for detail"
              : "Checks still in progress"}
          </p>
        </div>
      </div>
    ) : record.status === "failed" ? (
      <div className="record-footer">
        <span className="record-date">
          Created: {new Date(record.createdDate).toLocaleDateString()}
        </span>
        <span
          className="record-link"
          aria-disabled="true"
          style={{ cursor: "not-allowed", opacity: 0.75 }}
        >
          Details unavailable
        </span>
      </div>
    ) : (
      <div className="record-footer">
        <span className="record-date">
          Created: {new Date(record.createdDate).toLocaleDateString()}
        </span>
        <Link to={`/portal/cdd-records/${record.id}`} className="record-link">
          View Details
          <span className="record-link-arrow">→</span>
        </Link>
      </div>
    )}
  </div>
  );
};

export default RecentRecordCard;
